// AthleticForm Component
// Form for editing athletic profile information

import React, { useState, useRef, useEffect } from 'react';
import { FaRunning, FaRulerVertical, FaWeight, FaSearch, FaTimes } from 'react-icons/fa';
import { useSports } from '../../../hooks/useSports';
import styles from './AthleticForm.module.css';

// Fallback positions (used when API positions are empty or unavailable)
const FALLBACK_POSITIONS = {
  Soccer: ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Striker', 'Winger'],
  Basketball: ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  Baseball: ['Pitcher', 'Catcher', 'Infielder', 'Outfielder', 'Designated Hitter'],
  Football: ['Quarterback', 'Running Back', 'Wide Receiver', 'Tight End', 'Offensive Line', 'Defensive Line', 'Linebacker', 'Defensive Back'],
  Tennis: ['Singles', 'Doubles'],
  Golf: ['Individual'],
  Swimming: ['Freestyle', 'Backstroke', 'Breaststroke', 'Butterfly', 'Individual Medley', 'Relay'],
  'Track & Field': ['Sprinter', 'Distance Runner', 'Jumper', 'Thrower', 'Hurdler', 'Multi-Event'],
  Volleyball: ['Outside Hitter', 'Setter', 'Middle Blocker', 'Libero', 'Opposite Hitter'],
  Hockey: ['Center', 'Winger', 'Defenseman', 'Goaltender'],
  Lacrosse: ['Attack', 'Midfield', 'Defense', 'Goalie'],
  Wrestling: ['Freestyle', 'Greco-Roman'],
  Gymnastics: ['All-Around', 'Vault', 'Bars', 'Beam', 'Floor'],
  'Cross Country': ['Runner']
};

const AthleticForm = ({ data, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Use dynamic sports from API
  const { sportNames, getPositions, getEmoji, loading: sportsLoading } = useSports();
  
  // Get positions for a sport (with fallback)
  const getPositionsForSport = (sportName) => {
    const apiPositions = getPositions(sportName);
    return apiPositions.length > 0 ? apiPositions : (FALLBACK_POSITIONS[sportName] || ['Player']);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ensure data exists
  if (!data) {
    return <div>Loading form...</div>;
  }

  // Handle input changes
  const handleInputChange = (field, value) => {
    const updatedData = { ...data };
    
    // Special handling for height and weight to combine value and unit
    if (field === 'physicalAttributes.height' || field === 'physicalAttributes.heightUnit') {
      const currentHeight = data.physicalAttributes?.height || {};
      const currentHeightValue = typeof currentHeight === 'object' ? currentHeight.value : currentHeight;
      const currentHeightUnit = typeof currentHeight === 'object' ? currentHeight.unit : (data.physicalAttributes?.heightUnit || 'cm');
      
      const heightValue = field === 'physicalAttributes.height' ? value : currentHeightValue;
      const heightUnit = field === 'physicalAttributes.heightUnit' ? value : currentHeightUnit;
      
      if (!updatedData.physicalAttributes) {
        updatedData.physicalAttributes = { ...data.physicalAttributes };
      } else {
        updatedData.physicalAttributes = { ...updatedData.physicalAttributes };
      }
      
      // Store as an object with value and unit, remove old format fields
      updatedData.physicalAttributes.height = {
        value: heightValue,
        unit: heightUnit
      };
      
      // Clean up old format fields
      delete updatedData.physicalAttributes.heightUnit;
      
      onChange(updatedData);
      return;
    }
    
    if (field === 'physicalAttributes.weight' || field === 'physicalAttributes.weightUnit') {
      const currentWeight = data.physicalAttributes?.weight || {};
      const currentWeightValue = typeof currentWeight === 'object' ? currentWeight.value : currentWeight;
      const currentWeightUnit = typeof currentWeight === 'object' ? currentWeight.unit : (data.physicalAttributes?.weightUnit || 'kg');
      
      const weightValue = field === 'physicalAttributes.weight' ? value : currentWeightValue;
      const weightUnit = field === 'physicalAttributes.weightUnit' ? value : currentWeightUnit;
      
      if (!updatedData.physicalAttributes) {
        updatedData.physicalAttributes = { ...data.physicalAttributes };
      } else {
        updatedData.physicalAttributes = { ...updatedData.physicalAttributes };
      }
      
      // Store as an object with value and unit, remove old format fields
      updatedData.physicalAttributes.weight = {
        value: weightValue,
        unit: weightUnit
      };
      
      // Clean up old format fields
      delete updatedData.physicalAttributes.weightUnit;
      
      onChange(updatedData);
      return;
    }
    
    // Handle nested fields (supports multiple levels)
    if (field.includes('.')) {
      const parts = field.split('.');
      let current = updatedData;
      
      // Navigate to the parent object
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        } else {
          current[parts[i]] = { ...current[parts[i]] };
        }
        current = current[parts[i]];
      }
      
      // Set the final value
      current[parts[parts.length - 1]] = value;
    } else {
      updatedData[field] = value;
    }
    
    onChange(updatedData);
  };

  // Get selected sports (combining legacy primarySport and secondarySports into one array)
  const getSelectedSports = () => {
    const sports = [];
    if (data.primarySport) sports.push(data.primarySport);
    if (data.secondarySports) sports.push(...data.secondarySports);
    return [...new Set(sports)]; // Remove duplicates
  };

  // Handle sport selection
  const handleSportToggle = (sport) => {
    const currentSports = getSelectedSports();
    
    if (currentSports.includes(sport)) {
      // Remove sport
      const updatedSports = currentSports.filter(s => s !== sport);
      updateSports(updatedSports);
    } else {
      // Add sport
      updateSports([...currentSports, sport]);
    }
    
    setSearchTerm('');
  };

  // Update the sports data structure
  const updateSports = (sportsArray) => {
    const updatedData = { ...data };
    // Keep first sport as primary, rest as secondary
    updatedData.primarySport = sportsArray[0] || '';
    updatedData.secondarySports = sportsArray.slice(1);
    onChange(updatedData);
  };

  // Remove a sport tag
  const handleRemoveSport = (sport) => {
    const currentSports = getSelectedSports();
    const updatedSports = currentSports.filter(s => s !== sport);
    updateSports(updatedSports);
  };

  // Filter sports based on search
  const getFilteredSports = () => {
    const selected = getSelectedSports();
    return sportNames.filter(sport => 
      !selected.includes(sport) && 
      sport.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Handle array changes (for positions)
  const handleArrayChange = (field, value, checked) => {
    if (field === 'positions') {
      // Positions need to be objects with sport, position, and isPrimary
      handlePositionChange(value, checked);
    } else {
      // Other arrays (like jersey numbers) can be simple values
      const currentArray = data[field] || [];
      let newArray;
      
      if (checked) {
        newArray = [...currentArray, value];
      } else {
        newArray = currentArray.filter(item => item !== value);
      }
      
      handleInputChange(field, newArray);
    }
  };
  
  // Handle position changes (create proper object structure)
  const handlePositionChange = (positionName, checked) => {
    const currentPositions = data.positions || [];
    let newPositions;
    
    if (checked) {
      // Find which sport this position belongs to
      const selectedSports = getSelectedSports();
      let positionSport = '';
      
      for (const sport of selectedSports) {
        if (getPositionsForSport(sport)?.includes(positionName)) {
          positionSport = sport;
          break;
        }
      }
      
      // Create position object
      const positionObj = {
        sport: positionSport,
        position: positionName,
        isPrimary: currentPositions.length === 0  // First position is primary
      };
      
      newPositions = [...currentPositions, positionObj];
    } else {
      // Remove the position (match by position name)
      newPositions = currentPositions.filter(p => 
        (typeof p === 'string' ? p : p.position) !== positionName
      );
    }
    
    handleInputChange('positions', newPositions);
  };

  // Get positions grouped by sport
  const getGroupedPositions = () => {
    const selectedSports = getSelectedSports();
    return selectedSports
      .filter(sport => getPositionsForSport(sport).length > 0)
      .map(sport => ({
        sport: sport,
        positions: getPositionsForSport(sport)
      }));
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.sectionTitle}>
        <FaRunning /> Athletic Profile
      </h2>

      {/* Sports Selection */}
      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Sports</h3>
        
        {/* Multi-Select Dropdown */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Select Your Sports *
          </label>
          <p className={styles.helperText}>Search and select all sports you play</p>
          
          <div className={styles.multiSelectContainer} ref={dropdownRef}>
            {/* Search Input */}
            <div 
              className={styles.multiSelectInput}
              onClick={() => setIsDropdownOpen(true)}
            >
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search sports..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                className={styles.searchInput}
              />
            </div>

            {/* Dropdown List */}
            {isDropdownOpen && (
              <div className={styles.dropdownList}>
                {getFilteredSports().length > 0 ? (
                  getFilteredSports().map(sport => (
                    <div
                      key={sport}
                      className={styles.dropdownItem}
                      onClick={() => handleSportToggle(sport)}
                    >
                      {sport}
                    </div>
                  ))
                ) : (
                  <div className={styles.dropdownEmpty}>
                    {searchTerm ? 'No sports found' : 'All sports selected'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected Sports Tags */}
          {getSelectedSports().length > 0 && (
            <div className={styles.selectedSportsSection}>
              <p className={styles.helperText}>Click a sport to set it as your primary sport</p>
              <div className={styles.selectedTags}>
                {getSelectedSports().map((sport, index) => {
                  const isPrimary = sport === data.primarySport;
                  return (
                    <div 
                      key={sport} 
                      className={`${styles.tag} ${isPrimary ? styles.primaryTag : ''}`}
                      onClick={() => {
                        // Set as primary sport
                        const currentSports = getSelectedSports();
                        const reordered = [sport, ...currentSports.filter(s => s !== sport)];
                        updateSports(reordered);
                      }}
                      style={{ cursor: 'pointer' }}
                      title={isPrimary ? 'Primary Sport' : 'Click to set as primary'}
                    >
                      <span>{sport}</span>
                      {isPrimary && <span className={styles.primaryBadge}>PRIMARY</span>}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSport(sport);
                        }}
                        className={styles.tagRemove}
                        aria-label={`Remove ${sport}`}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Positions - Grouped by Sport */}
        {getGroupedPositions().length > 0 && (
          <div className={styles.formGroup}>
            <label className={styles.label}>Positions</label>
            <p className={styles.helperText}>Select the positions you play for each sport</p>
            
            <div className={styles.positionsContainer}>
              {getGroupedPositions().map(({ sport, positions }) => (
                <div key={sport} className={styles.sportPositionGroup}>
                  <h4 className={styles.sportPositionTitle}>{sport}</h4>
                  <div className={styles.checkboxGrid}>
                    {positions.map(position => {
                      // Check if position is selected (handle both string and object format)
                      const isSelected = (data.positions || []).some(p => 
                        typeof p === 'string' ? p === position : p.position === position
                      );
                      
                      return (
                        <label key={position} className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleArrayChange('positions', position, e.target.checked)}
                          />
                          <span className={styles.checkmark}></span>
                          {position}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Physical Attributes */}
      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>
          <FaRulerVertical /> Physical Attributes
        </h3>
        
        <div className={styles.attributeGrid}>
          {/* Height */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Height</label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                className={styles.input}
                value={data.physicalAttributes?.height?.value || data.physicalAttributes?.height || ''}
                onChange={(e) => handleInputChange('physicalAttributes.height', e.target.value)}
                placeholder="Height"
                min="0"
                step="0.1"
              />
              <select
                className={styles.unitSelect}
                value={data.physicalAttributes?.height?.unit || data.physicalAttributes?.heightUnit || 'cm'}
                onChange={(e) => handleInputChange('physicalAttributes.heightUnit', e.target.value)}
              >
                <option value="cm">cm</option>
                <option value="ft">ft</option>
              </select>
            </div>
          </div>

          {/* Weight */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaWeight /> Weight
            </label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                className={styles.input}
                value={data.physicalAttributes?.weight?.value || data.physicalAttributes?.weight || ''}
                onChange={(e) => handleInputChange('physicalAttributes.weight', e.target.value)}
                placeholder="Weight"
                min="0"
                step="0.1"
              />
              <select
                className={styles.unitSelect}
                value={data.physicalAttributes?.weight?.unit || data.physicalAttributes?.weightUnit || 'kg'}
                onChange={(e) => handleInputChange('physicalAttributes.weightUnit', e.target.value)}
              >
                <option value="kg">kg</option>
                <option value="lbs">lbs</option>
              </select>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AthleticForm;
